// content-storage.service.ts
import { Injectable } from '@angular/core';
import { BlockContent, StructureContent, VERSION_TYPE } from '../pages/models/content.model';

/**
 * Serwis zarządzający przechowywaniem struktur treści w localStorage
 * Implementuje system wersjonowania (original/working) dla danych
 */
@Injectable({
  providedIn: 'root',
})
export class ContentStorageService {
  private readonly MATRIX_PREFIX = 'matrix_';
  private readonly BLOCK_PREFIX = 'block_';

  /**
   * Rozszerzona metoda czyszczenia z diagnostyką
   * @param includeGlobalStyles - Czy usunąć również globalne style
   * @param logDetails - Czy logować szczegóły usuniętych elementów
   * @returns Liczba usuniętych elementów
   */
  clearAllContentData(includeGlobalStyles = true, logDetails = true): number {
    console.log('[ContentStorageService->clearAllContentData] Rozpoczęcie czyszczenia localStorage');

    const keysToRemove: string[] = [];
    const detailedLog: Record<string, string[]> = {
      blocks: [],
      matrix: [],
      globalStyles: [],
    };

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      let shouldRemove = false;

      if (key.startsWith(this.BLOCK_PREFIX)) {
        shouldRemove = true;
        detailedLog.blocks.push(key);
      } else if (key.startsWith(this.MATRIX_PREFIX)) {
        shouldRemove = true;
        detailedLog.matrix.push(key);
      } else if (includeGlobalStyles && key === 'GlobalStyles') {
        shouldRemove = true;
        detailedLog.globalStyles.push(key);
      }

      if (shouldRemove) {
        keysToRemove.push(key);
      }
    }

    if (logDetails && keysToRemove.length > 0) {
      console.log('[ContentStorageService->clearAllContentData] Szczegóły czyszczenia:');
      console.log(`  - Struktury blokowe (${detailedLog.blocks.length}):`, detailedLog.blocks);
      console.log(`  - Struktury macierzowe (${detailedLog.matrix.length}):`, detailedLog.matrix);
      console.log(`  - Globalne style (${detailedLog.globalStyles.length}):`, detailedLog.globalStyles);
    }

    this.removeItems(keysToRemove);

    console.log(`[ContentStorageService->clearAllContentData] Zakończono czyszczenie - usunięto ${keysToRemove.length} elementów`);
    return keysToRemove.length;
  }

  /**
   * Zapisuje strukturę macierzową z dwoma wersjami (original/working)
   * @param content - Dane macierzy do zapisania
   * @remarks Używa prefiksu 'matrix_' dla kluczy w localStorage
   */
  saveMatrixContent(content: BlockContent): void {
    const baseKey = `${this.MATRIX_PREFIX}${content.id}`;
    localStorage.setItem(`${baseKey}_original`, JSON.stringify(content));
    localStorage.setItem(`${baseKey}_working`, JSON.stringify(content));
  }

  /**
   * Zapisuje strukturę blokową z głębokim kopiowaniem
   * @param content - Obiekt struktury do zapisania
   * @param structure_type - Typ struktury, który ma być użyty (zastępuje pole w content)
   * @remarks Automatycznie tworzy głębokie kopie struktury przed zapisem
   */
  saveBlockContent(content: StructureContent, structure_type = 'page'): void {
    console.log('[ContentStorageService->saveBlockContent] content', content);

    const deepClonedStructure = typeof structuredClone === 'function'
      ? structuredClone(content.structure || [])
      : JSON.parse(JSON.stringify(content.structure || []));

    // Zabezpieczenie przed undefined dla content.seo
    content.seo = content.seo || {
      meta_tags: [],
      meta_title: {},
      meta_description: {},
      meta_tags_ids: [],
      slug: {},
      isIndexed: false,
    };
    content.seo.meta_tags_ids = content.seo.meta_tags_ids ?? [];

    const structureContent: StructureContent = {
      ...content,
      structure_type,
      structure: deepClonedStructure,
    };

    console.log('[ContentStorageService->saveBlockContent] structureContent', structureContent);
    this.saveContent(this.BLOCK_PREFIX, structureContent);
  }

  /**
   * Uniwersalna metoda zapisu struktury z prefiksem
   * @param prefix - Prefix dla klucza (np. 'block_')
   * @param content - Struktura do zapisania
   * @remarks Tworzy dwie wersje: original i working
   */
  saveContent(prefix: string, content: StructureContent): void {
    const baseKey = `${prefix}${content.id}`;
    localStorage.setItem(`${baseKey}_original`, JSON.stringify(content));
    localStorage.setItem(`${baseKey}_working`, JSON.stringify(content));
    console.log('[ContentStorageService->saveContent] baseKey', prefix);
    console.log('[ContentStorageService->saveContent] content', content);
  }

  /**
   * Pobiera aktualną wersję struktury macierzowej
   * @param pageId - ID strony
   * @returns Struktura macierzowa lub null
   */
  getMatrixContent(pageId: number): BlockContent | null {
    return this.getContent<BlockContent>(`${this.MATRIX_PREFIX}${pageId}_working`);
  }

  /**
   * Generyczna metoda pobierania danych z localStorage
   * @param key - Klucz do odczytu
   * @returns Sparsowane dane lub null w przypadku błędu
   */
  getContent<T>(key: string): T | null {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error(`Błąd parsowania danych dla klucza ${key}:`, error);
      return null;
    }
  }

  /**
   * Pobiera struktury blokowe według wersji
   * @param versionType - Typ wersji (ALL/WORKING/ORIGINAL)
   * @returns Lista struktur z określoną wersją
   * @remarks Filtruje wpisy w localStorage po prefixie i sufixie
   */
  getAllBlockStructuresFromLocalStorage(
    versionType: VERSION_TYPE = VERSION_TYPE.ALL,
  ): StructureContent[] {
    const structures: StructureContent[] = [];
    const versionSuffixes = versionType === VERSION_TYPE.ALL
      ? ['_original', '_working']
      : [`_${versionType.toLowerCase()}`];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(this.BLOCK_PREFIX) ||
        !versionSuffixes.some(suffix => key.endsWith(suffix))) continue;

      try {
        const content = JSON.parse(localStorage.getItem(key) || '{}');
        if (content?.structure) {
          structures.push(content as StructureContent);
        }
      } catch (error) {
        console.warn('Błąd parsowania klucza:', key, error);
      }
    }

    return structures;
  }

  /**
   * Zwraca wszystkie klucze struktur blokowych
   * @returns Tablica kluczy localStorage
   */
  getAllBlockStructureKeys(): string[] {
    return Array.from({length: localStorage.length}, (_, i) => localStorage.key(i)!)
      .filter(key => key?.startsWith(this.BLOCK_PREFIX));
  }

  /**
   * Wyszukuje client_id lub clientId w zapisanych strukturach (może być stringiem lub liczbą)
   * @returns client_id jako string lub null, jeśli nie znaleziono żadnego poprawnego identyfikatora
   * @remarks Przeszukuje zarówno struktury macierzowe jak i blokowe, obsługuje różne formaty i typy
   */
  findClientIdInStored(): string | null {
    console.log('[ContentStorageService->findClientIdInStored] Start');
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      // Sprawdzamy tylko struktury macierzowe lub blokowe w wersji 'original'
      if (
        !key ||
        !(key.startsWith(this.MATRIX_PREFIX) || key.startsWith(this.BLOCK_PREFIX)) ||
        !key.endsWith('_original')
      ) {
        continue;
      }
      console.log(`[findClientIdInStored] Analizuję klucz: ${key}`);

      const content = this.getContent<any>(key);
      if (!content || typeof content !== 'object') {
        console.warn(`[findClientIdInStored] Brak danych lub nieprawidłowy typ dla klucza: ${key}`, content);
        continue;
      }

      // Obsługa różnych możliwych nazw i typów
      let clientId: string | number | undefined = undefined;
      if ('client_id' in content) {
        clientId = content.client_id;
        console.log(`[findClientIdInStored] Znaleziono pole client_id:`, clientId);
      } else if ('clientId' in content) {
        clientId = content.clientId;
        console.log(`[findClientIdInStored] Znaleziono pole clientId:`, clientId);
      } else {
        console.log(`[findClientIdInStored] Brak pola client_id ani clientId w kluczu: ${key}`);
      }

      // Akceptujemy tylko string lub number, odrzucamy inne typy (np. null, undefined, obiekt, tablica)
      if (
        (typeof clientId === 'string' && clientId.trim() !== '') ||
        (typeof clientId === 'number' && !isNaN(clientId))
      ) {
        console.log(`[findClientIdInStored] Zwracam clientId: ${String(clientId)} z klucza: ${key}`);
        return String(clientId);
      } else if (clientId !== undefined) {
        console.warn(`[findClientIdInStored] Nieprawidłowy typ clientId (${typeof clientId}) lub pusta wartość w kluczu: ${key}`, clientId);
      }
    }
    console.warn('[ContentStorageService->findClientIdInStored] Nie znaleziono poprawnego client_id/clientId w żadnej strukturze');
    return null;
  }

  /**
   * Wyszukuje ustawienia strony w przechowywanych strukturach
   * @returns Struktura strony lub null jeśli nie znaleziono
   */
  getPageSettingsFromStructure(): StructureContent | null {
    console.log('[ContentStorageService] Wyszukiwanie struktury strony...');

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.BLOCK_PREFIX)) {
        const item = localStorage.getItem(key);
        if (item) {
          try {
            const structure = JSON.parse(item) as StructureContent;
            if (structure.structure_type === 'page' && key.includes('_working')) {
              console.log('[ContentStorageService] Znaleziono strukturę strony:', key);
              return structure;
            }
          } catch (e) {
            console.error('[ContentStorageService] Błąd parsowania struktury:', key, e);
          }
        }
      }
    }
    return null;
  }

  /**
   * Zapisuje przekazaną strukturę strony do localStorage jako wersję roboczą.
   * @param content Obiekt typu StructureContent, który ma zostać zapisany
   * @remarks Klucz jest generowany na podstawie prefixu, id oraz sufiksu '_working'
   */
  savePageSettingsToStructure(content: StructureContent): void {
    const key = `${this.BLOCK_PREFIX}${content.id}_working`;
    localStorage.setItem(key, JSON.stringify(content));
  }

  /**
   * Usuwa podane klucze z localStorage
   * @param keys - Tablica kluczy do usunięcia
   */
  removeItems(keys: string[]): void {
    keys.forEach(key => localStorage.removeItem(key));
  }


}
